import { useEffect, useMemo, useState } from "react";
import { Tooltip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import Datatable from "../Common/Datatable";
import CustomConfirm from "../../components/Common/CustomConfirm";
import {
  getColumns,
  initialSort,
  initialFilter,
} from "./actionItemTableConfig";
import useGlobalContext from "../../context/useGlobalContext";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { formatDateOnly, getLoggedInUserDomain, getUTCTimeZoneOffset, PRIORITY_OPTIONS } from "../../utils/utils";
import { ExclusiveColumnFilterContext } from "../Common/customFilter";
import { ActionItemType, ConvertTaskAction, Tag } from "../../utils/types";
import dayjs from "dayjs";
import { UserTeamData } from "../../utils/types";
import ActionItemService from "../../services/actionItems/ActionItemService";
import InlineTaskCreation from "./InlineTaskCreation";
import UserSelector from "./UserSelector";
import PrioritySelector from "./PrioritySelector";
import ActionItemsStatusCell from "./ActionItemsStatusCell";
import DueDateIcon from "../../assets/action-items/due-date-calendar.svg";
import CommonDatePicker from "../Common/CommonDatePicker";
import SoloCoachIcon from "../../assets/action-items/coach-icon.svg";
import ExecSponseIcon from "../../assets/action-items/sponser-icon.svg";
import AccountIcon from "../../assets/action-items/client-icon.svg";
import AccountSelector from "./AccountSelector";
import ActionItemsTagsCell from "./ActionItemsTagsCell";
import TagsListIcon from "../../assets/action-items/tag-list.svg";
import AssigneeIcon from "../../assets/action-items/assignee-icon.svg";
import FeedbackPopup from "../Common/FeedbackPopUp";
import { patchConvertedActionItemRows } from "../../utils/subtaskUtils";


const isPlaceholderActionItemId = (id?: string | null) =>
  typeof id === "string" && (id === "__add_task__" || id.startsWith("__add_sub_task_"));

const isGroupedViewValue = (groupBy?: string) =>
  Boolean(groupBy && groupBy !== "undefined");

const collectPlaceholderRowIds = (list: any[] = []): string[] =>
  list
    .filter((row) => isPlaceholderActionItemId(row?.id))
    .map((row) => row.id);

const collectDisabledActionItemIds = (items: any[] = []): string[] =>
  items.flatMap((item) =>
    (item?.canEdit === false || item?.isDeleted === true) && item?.actionItemId
      ? [item.actionItemId]
      : []
  );

const mapActionItemRow = (
  item: any,
  type: any,
  startEndMeetingTime: any,
  view: any,
  newUserTeamData: any,
  activeUsers: any,
  clientList: any,
  tagsList: any[] | undefined,
  spacesList: any[] | undefined,
  handleUnsubscribe?: (row: any) => void,
): any => {
  return {
    id: item.actionItemId,
    description: item.title,
    meeting_id: item.interactionId,
    summary: item.summary,
    dueDate: item.dueDate,
    revised_due_date: item.revisedDueDate,
    assignee: item.assigneePorId,
    assigneePorId: item.assigneePorId,
    meeting_title: item.summary,
    assignee_name: item.assigneeName,
    status: item.status,
    attachments: item.attachments,
    creator: item.creatorPorId,
    creator_name: item.creatorName,
    creator_email: item.creatorEmail,
    priority: item.priority,
    momlink: item.momlink,
    sponsor: item.sponsorPorId,
    tags: item.tags,
    subscribers: item.subscribers,
    is_ai_generated: item?.isAiGenerated,
    actionItemDescription: item.description,
    clientDomain: item.clientDomain,
    clientOrgId: item.clientOrgId ?? null,
    client_domain: (item?.clientName as string) ?? "Internal",
    type: type,
    startEndMeetingTime: startEndMeetingTime,
    view: view,
    newUserTeamData: newUserTeamData,
    activeUsers: activeUsers,
    coach: item.coachPorId,
    coachName: item.coachName,
    coachEmail: item.coachEmail,
    coachPictureUrl: item.coachPictureUrl,
    assignorEmail: item.assignorEmail,
    clientList: clientList,
    tagsList: tagsList,
    handleUnsubscribe: handleUnsubscribe,
    spacesList: spacesList,
    subscribedUserGroups: item.subscribedUserGroups,
    clientName: item.clientName,
    assigneeEmail: item.assigneeEmail,
    assigneePictureUrl: item.assigneePictureUrl,
    createdAt: item.createdAt,
    isTask: item?.isTask ?? false,
    isDiscarded: item?.isDeleted ? true : item?.isDiscarded ?? false,
    isApproved: item?.isTask === true,
    canEdit: item?.isDeleted || item?.isDiscarded ? false : item?.canEdit,
    isSystemGeneratedTask: item.isSystemGeneratedTask,
    assigneeUpdatedAt: item.assigneeUpdatedAt,
    isSubTask: item?.isSubTask === true || Boolean(item?.parentActionItemId),
    parentDueDate: item?.parentDueDate ?? item?.parentActionItem?.dueDate ?? null,
    parentActionItemId: item?.parentActionItemId ?? null,
    parentActionItem: item?.parentActionItem,
    subtaskCount: item?.subtaskCount,
  };
};

const generateRows = (
  actionItems: any,
  type: any,
  startEndMeetingTime: any,
  view: any,
  newUserTeamData: any,
  activeUsers: any,
  clientList: any,
  tagsList: any[] | undefined,
  spacesList: any[] | undefined,
  handleUnsubscribe?: (row: any) => void,
) => {
  const list = Array.isArray(actionItems) ? actionItems : [];
  return list.map((item: any) =>
    mapActionItemRow(
      item,
      type,
      startEndMeetingTime,
      view,
      newUserTeamData,
      activeUsers,
      clientList,
      tagsList,
      spacesList,
      handleUnsubscribe,
    )
  );
};

interface ActionItemsProps {
  type: "MY_ACTION_ITEMS" | "TEAM_ACTION_ITEMS" | "SUBSCRIBED_ACTION_ITEMS";
  isLoading: boolean;
  actionItems?: any[];
  totalRecord: number;
  handleActionItemSelection?: (actionItem: any, canEdit?: boolean, isSubTask?: boolean) => void;
  handleActionItemPreview?: (actionItem: any, isSubTask?: boolean) => void;
  pageSize: number;
  onSearch?: (value: string) => void;
  onPageChange: (value: number) => void;
  onFilter: (value: {
    date: { start_time: string; end_time: string } | undefined;
    status: undefined | string;
  }) => void;
  onSort?: (value: { sort_field: string; sort_order: string }) => void;
  actionItemType: string;
  onTypeChange: (value: ActionItemType) => void;
  onPageSizeChange?: (newPageSize: number) => void;
  startEndMeetingTime?: { start_time: string; end_time: string }
  view?: any;
  sortorder?: (newSort: any) => void;
  selectedTab?: string;
  newUserTeamData?: UserTeamData | null;
  refetchSubscribedActionItems?: () => void;
  handleEditIconClick?: (actionItem: any) => void;
  tableMinHeight?: string;
  showSpinner?: boolean
  setShowSpinner?: React.Dispatch<React.SetStateAction<boolean>>
  columnFilters?: {
    assignee: string[];
    coach: string[];
    dueDate: string;
    priority: string[];
    account: string[];
    actionItem: string;
    status: string[];
    tags: string[];
  },
  updateColumnFilter?: (columnKey: string, value: any) => void
  sort?: { sort_field: string; sort_order: "asc" | "desc" },
  onSortChange?: (columnKey: string, direction: "asc" | "desc") => void,
  activeUsers?: any[];
  clientList?: any[];
  setSelectedActionItemIds?: React.Dispatch<React.SetStateAction<any[]>>
  selectedActionItemIds?: any[];
  bulkUpdateValues?: Record<string, any> | undefined;
  tagsList?: any[];
  groupByValues?: {
    groupByInteractionId: string | null;
    groupByClientDomain: { clientName: string | null; orgId: string | null } | null;
    groupByAssigneeId: string | null;
    groupByStatus: string | null;
},
showAddTask?: boolean
maxRowsConfig?:{
  maxVisibleRows: number;
  rowHeight: number;
  pageSizeOptions: number[];
}
spacesList?: any[];
groupBy?: string;
columnVisibility?: Record<string, boolean>;
openTaskDialog?: boolean;
enableRowSelection?: boolean;
tableMaxHeight?: string;
isReadOnly?: boolean;
isCountLoading?: boolean;
feedbackEnabled?: boolean;
state?: {
  columnOrder?: string[];
};
onColumnOrderChange?: (newOrder: string[]) => void;
}
const ActionItems: React.FC<ActionItemsProps> = ({
  type,
  isLoading,
  actionItems,
  totalRecord,
  handleActionItemSelection,
  handleActionItemPreview,
  pageSize,
  onSearch,
  onPageChange,
  onFilter,
  actionItemType,
  onTypeChange,
  onPageSizeChange,
  startEndMeetingTime,
  view,
  sortorder,
  selectedTab, 
  newUserTeamData,
  refetchSubscribedActionItems,
  handleEditIconClick,
  tableMinHeight,
  setShowSpinner,
  showSpinner,
  columnFilters,
  updateColumnFilter,
  sort,
  onSortChange,
  activeUsers,
  clientList,
  setSelectedActionItemIds,
  selectedActionItemIds,
  bulkUpdateValues,
  tagsList,
  groupByValues,
  showAddTask = false,
  maxRowsConfig,
  spacesList,
  groupBy,
  columnVisibility,
  openTaskDialog,
  enableRowSelection = true,
  tableMaxHeight,
  isReadOnly = false,
  isCountLoading = false,
  feedbackEnabled = false,
  state,
  onColumnOrderChange,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<any[]>([]);
  const [highlightedCells, setHighlightedCells] = useState<Record<string, Record<string, boolean>>>({});
  const { calendar, timeRange, calendarsData, accountDetails } = useGlobalContext();
  const { showSnackbar } = useDisplaySnackbar();
  const [actionItemToBeUpdated, setActionItemToBeUpdated] =
    useState<string>("");
  const [actionItemToDelete, setActionItemToDelete] = useState<any | null>(
    null
  );
  const [openDialog, setOpenDialog] = useState(false);
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const [addTaskAnchorEl, setAddTaskAnchorEl] = useState<HTMLElement | null>(null);
  const [persistedGroupBy, setPersistedGroupBy] = useState({
    assignee: null as string | null,
    clientDomain: { clientName: null, orgId: null } as {
      clientName: string | null;
      orgId: string | null;
    } | null,
    interactionId: null as string | null,
    status : null as string | null,
  });  
  const dropdownUserData = {
    "usersList": activeUsers || [],
    "accountList": clientList || [],
    "tagsList": tagsList || [],
    "spacesList": spacesList || [],
  }
  useEffect(()=>{
    if(selectedTab){
      setAddTaskAnchorEl(null)
      setDueDateAnchorEl(null);
    }
  }, [selectedTab])
  const notAllowedActionItemids = collectDisabledActionItemIds(actionItems || []);
  const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");
  const loggedInUserDomain = getLoggedInUserDomain();
  const [inlineTaskData, setInlineTaskData] = useState({
    title: "",
    assignee: persistedGroupBy.assignee 
      ?? groupByValues?.groupByAssigneeId 
      ?? loggedInProfileOrgId,
    dueDate: tomorrow,
    priority: "medium",
    status: loggedInUserDomain==="anarix.io" ? "open": "to_do",
    tags: [] as Tag[],
    soloCoach: "",
    execSponsor: "",
    client: {
      orgId: persistedGroupBy.clientDomain?.orgId
        ?? null,
      name: persistedGroupBy.clientDomain?.clientName
        ?? null,
    },
  });
  const [dueDateAnchorEl, setDueDateAnchorEl] = useState<HTMLElement | null>(null);

  const [discardPopupOpen, setDiscardPopupOpen] = useState(false);
  const [discardRow, setDiscardRow] = useState<any | null>(null);
  const [feedbackReference, setFeedbackReference] = useState<string>("");
  const { featureMap } = useGlobalContext();
  const userFeedback = featureMap?.idp_user_feedback === true;
  const columnOrder = state?.columnOrder;

  useEffect(() => {
    const { groupByAssigneeId, groupByClientDomain, groupByInteractionId, groupByStatus } = groupByValues || {};
    const hasAnyValue =
    groupByAssigneeId != null ||
    groupByInteractionId != null ||
    (groupByClientDomain?.clientName != null || groupByClientDomain?.orgId != null || groupByStatus != null);
    if (!hasAnyValue) return;
      setPersistedGroupBy({
        assignee: groupByAssigneeId || null,
        clientDomain: {
          clientName: groupByClientDomain?.clientName ?? null,
          orgId: groupByClientDomain?.orgId ?? null,
        },
        interactionId: groupByInteractionId || null,
        status: groupByStatus ?? null,
      });
  }, [groupByValues]);

  useEffect(()=> {
    if (openTaskDialog) {
      setAddTaskAnchorEl(null);
      setDueDateAnchorEl(null);
    }
  }, [openTaskDialog]);

  useEffect(() => {
    if (!addTaskAnchorEl) {
      setDueDateAnchorEl(null);
    }
  }, [addTaskAnchorEl]);
  
  useEffect(() => {
    if (groupBy === "" || groupBy === undefined) {
      setPersistedGroupBy({
        assignee: null,
        clientDomain: null,
        interactionId: null,
        status: null,
      });
    }
  }, [groupBy]);
  const combinedColumnVisibilty = {
    ...columnVisibility,
    subscribe: false,
  }
  const [pinnedColumns] = useState({
  left: ['serialnumber','description'],
});
  const isLargeUp = useMediaQuery(theme.breakpoints.up("lg"));

  const { mutate: mutateSubscribeActionItem } = 
  ActionItemService.useSubscribeActionItem({
    onSuccess: (_response: any, variables: any) => {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === variables.actionItemId
            ? { ...row, isSubscribed: variables.isSubscribed }
            : row
        )
      );
    },
    onError: () => {
      showSnackbar("error", "Error while updating subscription.");
    },
  });

  const { mutate: mutateConvertActionItemToTask } =
  ActionItemService.useConvertActionItemToTask({
    onMutate: () => {
      setShowSpinner?.(true);
    },
    onSuccess: (response: any, variables: any) => {
      const message =
        response?.data?.message ||
        "Action items successfully converted to task.";
      showSnackbar("success", message);

      setRows((prevRows) =>
        patchConvertedActionItemRows(
          prevRows,
          variables.actionItemIds ?? [],
          variables.action === ConvertTaskAction.ACCEPTED
        )
      );

      queryClient.invalidateQueries({ queryKey: ["get_action_items"] });
      queryClient.invalidateQueries({ queryKey: ["get_my_action_items"] });
      queryClient.invalidateQueries({
        queryKey: ["get_meetings_with_review_actions"],
      });
      if (groupBy) {
        queryClient.invalidateQueries({
          queryKey: ["get_group_by_action_items"],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["get_action_item_count"],
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        "Error while converting action item to task.";
      showSnackbar("error", message);
    },
    onSettled: () => {
      setShowSpinner?.(false);
    },
  });
  
  useEffect(() => {
    setInlineTaskData({
      title: "",
      assignee: persistedGroupBy.assignee 
        ?? groupByValues?.groupByAssigneeId 
        ?? loggedInProfileOrgId,
      dueDate: tomorrow,
      priority: "medium",
      status: persistedGroupBy.status ?? (loggedInUserDomain === "anarix.io" ? "open" : "to_do"),
      tags: [],
      soloCoach: "",
      execSponsor: "",
      client: {
        orgId: groupByValues?.groupByClientDomain?.orgId ?? persistedGroupBy.clientDomain?.orgId
        ?? null,
        name: groupByValues?.groupByClientDomain?.clientName ?? persistedGroupBy.clientDomain?.clientName ??
        null,
      },
    });
  }, [groupByValues, loggedInProfileOrgId]);

  useEffect(() => {
    setInlineTaskData(prev => ({
      ...prev,
      status:
        persistedGroupBy.status ?? (loggedInUserDomain==="anarix.io" ? "open": "to_do"),
    }));
  }, [persistedGroupBy]);
  
  const handleUnsubscribe = (row: any) => {
    mutateSubscribeActionItem({
      actionItemIds: [row.id],
      subscriberProfileOrgIdId: loggedInProfileOrgId,
      isSubscribed: false,
    }, {
      onSuccess: () => {
        showSnackbar("success", "Successfully unsubscribed.");
        refetchSubscribedActionItems?.();
      },
      onError: () => {
        showSnackbar("error", "Error unsubscribing.");
      }
    });
  };

  const handleConvertToTask = (actionItemIds: string[], action: ConvertTaskAction, row: any) => {
    const realActionItemIds = (actionItemIds || []).filter(
      (id) => id && !isPlaceholderActionItemId(id) && !id.startsWith("__add_sub_task_")
    );
    if (!realActionItemIds.length) return;

    const interactionId =
      row?.original?.meeting_id || row?.meeting_id || row?.original?.interactionId;

    if (!interactionId) {
      showSnackbar("error", "Unable to update action item.");
      return;
    }

    mutateConvertActionItemToTask({
      interactionId: interactionId,
      actionItemIds: realActionItemIds,
      action,
    });
  };

  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  useEffect(() => {
    if (openTaskDialog) {
      setOpenFilterColumn(null);
    }
  }, [openTaskDialog]);
  const exclusiveFilterValue = useMemo(
    () => ({
      activeKey: openFilterColumn,
      setActiveKey: setOpenFilterColumn,
    }),
    [openFilterColumn, setOpenFilterColumn]
  );

  const columns = useMemo(() =>
    getColumns(isLargeUp, theme,  [], columnFilters, updateColumnFilter, dropdownUserData, sort,
      onSortChange, bulkUpdateValues, selectedActionItemIds, isReadOnly, {
      selectedTab,
      highlightedCells,
      groupBy,
      isGroupByView: isGroupedViewValue(groupBy),
      highlightMissingFields: (row: any, fields: Record<string, boolean>) => {
        setHighlightedCells((prev) => ({
          ...prev,
          [row.id]: {
            ...(prev[row.id] || {}),
            ...fields,
          },
        }));
      },
      removeHighlight: (rowId: string, field: string) => {
        setHighlightedCells((prev) => ({
          ...prev,
          [rowId]: {
            ...(prev[rowId] || {}),
            [field]: false,
            ...(field === "assignee" ? { open_assignee: false } : {}),
            ...(field === "dueDate" ? { autoOpenDueDatePicker: false } : {}),
          },
        }));
      },
      selectRowCallback: (row: any, canEdit: boolean) => {
        const item = row?.original ?? row;
        canEdit
          ? handleActionItemSelection && handleActionItemSelection(item, canEdit, false)
          : handleActionItemPreview && handleActionItemPreview(item, false);
      },
      deleteRowCallback: (row: any) => {
        handleActionItemDelete(row.original);
      },
      updateDueDateCallback: (row: any, value: string) => {
        setActionItemToBeUpdated(row.id);
        setHighlightedCells((prev) => ({
          ...prev,
          [row.id]: {
            ...(prev[row.id] || {}),
            dueDate: false,
            autoOpenDueDatePicker: false,
          },
        }));
        const updatePayload: any = {
          dueDate: formatDateOnly(value),
        };
        mutateActionItemUpdate(updatePayload);
      },
      updateAssigneeCallback: (row: any, value: string) => {
        setActionItemToBeUpdated(row.id);
        setHighlightedCells((prev) => ({
          ...prev,
          [row.id]: {
            ...(prev[row.id] || {}),
            assignee: false,
            open_assignee: false,
          },
        }));
        const payload: any = {
          assigneePorId: value,
        };
      
        mutateActionItemUpdate(payload);
      },      
      updateStatusCallback: (row: any, value: string) => {
        if (
          row.original.status === "draft" &&
          row.original.assignee === "" &&
          row.original.revised_due_date === ""
        ) {
          showSnackbar(
            "warning",
            "Please ensure that a due date and assignee are set to track progress effectively."
          );
        } else {
          setActionItemToBeUpdated(row.id);
          const payload: any = {
            status: value,
          };
          mutateActionItemUpdate(payload);
        }
      },
      updatePriorityCallback: (row: any, value: string) => {
        setActionItemToBeUpdated(row.id);
        const payload: any = {
          priority: value,
        };
        mutateActionItemUpdate(payload);
      },
      onEditClick: (row: any) => {
        if(handleEditIconClick)
          handleEditIconClick(row)
      },
      handleUnsubscribe,
      updateCoachCallback: (row: any, value: string, removeFields?: string[]) => {
        setActionItemToBeUpdated(row.id);
        const payload: any = removeFields
        ? { removeFields }
        : { coachPorId: value};
      
        mutateActionItemUpdate(payload);
      },
      updateAccountCallback: (row: any, value: string, removeFields?: string[]) => {
        setActionItemToBeUpdated(row.id);
        const payload: any = removeFields
        ? { removeFields }
        : { clientOrgId: value};
      
        mutateActionItemUpdate(payload);
      },
      updateDescriptionCallBack: (row: any, value: string) => {
        setActionItemToBeUpdated(row.id);
        const payload: any = {
          title: value,
        };
      
        mutateActionItemUpdate(payload);
      },
      updateSponsorCallback: (row: any, value: string, removeFields?: string[]) => {

        setActionItemToBeUpdated(row.id);

        const payload: any = removeFields
        ? { removeFields } 
        : {sponsorPorId: value, // added this field
        };

        mutateActionItemUpdate(payload);
      },  
      updateTagsCallBack: (row: any, value: string) => {
        setActionItemToBeUpdated(row.id);
        const payload: any = {
          tagIds: value,
        };
        mutateActionItemUpdate(payload);
      },
      updateSubscribersCallBack: (
        row: any,
        value: { actionSubscribers: string[]; deleteSubscribers: string[]; addSpaces: string[]; deleteSpaces: string[]; }
      ) => {
        setActionItemToBeUpdated(row.id);
        const payload = {
          id: row.id,
          actionSubscribersPorIds: value.actionSubscribers,
          deleteSubscribersPorIds: value.deleteSubscribers,
          addSpaces: value.addSpaces,
          deleteSpaces: value.deleteSpaces,
        };
        mutateActionItemUpdate(payload);
      },
      onAddTask: (element: HTMLElement) => setAddTaskAnchorEl(element),
      onColumnFilterOpen:()=>{handleInlineCancel()},
      acceptCallback: (row: any) => {
        const actionItemId = row?.original?.id || row?.id;
        if (!actionItemId || isPlaceholderActionItemId(actionItemId)) return;
        setRows((prevRows) =>
          patchConvertedActionItemRows(prevRows, [actionItemId], true)
        );
        setTimeout(() => {
          handleConvertToTask([actionItemId], ConvertTaskAction.ACCEPTED, row);
        }, 1000);
      },
      discardCallback: (row: any) => {
        const actionItemId = row?.original?.id || row?.id;

        if (!actionItemId || isPlaceholderActionItemId(actionItemId)) return;

        if (userFeedback && feedbackEnabled) {
          setDiscardRow(row);
          const actionItemTitle =
            row?.original?.description;
          setFeedbackReference(actionItemTitle);
          setDiscardPopupOpen(true);
        } else {
          // OLD FLOW → discard immediately
          setRows((prevRows) =>
            patchConvertedActionItemRows(prevRows, [actionItemId], false)
          );

          setTimeout(() => {
            handleConvertToTask([actionItemId], ConvertTaskAction.DISCARDED, row);
          }, 1000);
        }
          },
      showSnackbar,
      openTaskDialog,
    }, openFilterColumn, setOpenFilterColumn),
    [
      isLargeUp,
      theme,
      calendarsData,
      columnFilters,
      updateColumnFilter,
      dropdownUserData,
      handleActionItemSelection,
      handleActionItemPreview,
      highlightedCells,
      openFilterColumn,
      setOpenFilterColumn,
      groupBy,
      selectedTab,
      openTaskDialog,
    ]
  );

  const handleDiscardSubmit = () => {
    if (!discardRow) return;
    const actionItemId = discardRow?.original?.id || discardRow?.id;
    // Show discarded badge immediately
    setRows((prevRows) =>
      patchConvertedActionItemRows(prevRows, [actionItemId], false)
    );
    setDiscardPopupOpen(false);
    setTimeout(() => {
      handleConvertToTask([actionItemId], ConvertTaskAction.DISCARDED, discardRow);
    }, 1000);
    setDiscardRow(null);
    setFeedbackReference("");
    showSnackbar("success", "Feedback submitted successfully.");
  };

  const { mutate: mutateActionItemUpdate } =
    ActionItemService.useUpdateActionItem([actionItemToBeUpdated], {
      onMutate: () => {
        setShowSpinner?.(true);
        sessionStorage.setItem('actionItemUpdated', JSON.stringify(true));
      },
      onSuccess: () => {
        type === "MY_ACTION_ITEMS" &&
          queryClient.invalidateQueries({
            queryKey: ["get_my_action_items"],
          });
        type === "TEAM_ACTION_ITEMS" &&
          queryClient.invalidateQueries({
            queryKey: ["get_my_action_items"],
          });
        type === "SUBSCRIBED_ACTION_ITEMS" &&
          queryClient.invalidateQueries({
            queryKey: ["get_my_action_items"],
          });
        queryClient.invalidateQueries({
          queryKey: ["get_meetings_with_review_actions"],
        });
        queryClient.invalidateQueries({
          queryKey: ["get_action_item_count"],
        });
        const searchPayload = sessionStorage.getItem('globalSearchPayload');
        if (searchPayload) {
          const parsedParams = JSON.parse(searchPayload);

          queryClient.invalidateQueries({
            queryKey: [
              'get_global_search_results',
              `${parsedParams.search_text}_${parsedParams.email_id}_${parsedParams.calendar_domain}`
            ]
          }).then(() => {
            queryClient.refetchQueries({
              queryKey: [
                'get_global_search_results',
                `${parsedParams.search_text}_${parsedParams.email_id}_${parsedParams.calendar_domain}`
              ]
            });
          });
        }
        if(groupBy){
          queryClient.invalidateQueries({
            queryKey: ["get_group_by_action_items"],
          });
        }
        if (actionItemToBeUpdated) {
          setHighlightedCells((prev) => {
            const next = { ...prev };
            delete next[actionItemToBeUpdated];
            return next;
          });
        }
        showSnackbar("success", "Task updated successfully.");
        setActionItemToBeUpdated("");
      },
      onError: (_error: string) => {
        showSnackbar("error", "Error while updating task");
      },
      onSettled: () => {
        setShowSpinner?.(false);
        setActionItemToBeUpdated("");
      },
    });

  const { mutate: mutateActionItemDelete } =
    ActionItemService.useDeleteActionItem(
      actionItemToDelete?.id || "",
      {
        onMutate: () => {
          setShowSpinner?.(true);
        },
        onSuccess: () => {
          type === "MY_ACTION_ITEMS" &&
            queryClient.invalidateQueries({
              queryKey: ["get_my_action_items"],
            });
          type === "TEAM_ACTION_ITEMS" &&
            queryClient.invalidateQueries({
              queryKey: ["get_my_action_items"],
            });
          queryClient.invalidateQueries({
            queryKey: ["get_action_item_count"],
          });
          showSnackbar("success", "Task deleted successfully");
        },
        onError: (error: string) => {
          showSnackbar("error", `Error while deleting task: ${error}`);
        },
        onSettled: () => {
          handleClose();
          setShowSpinner?.(false);
        },
      }
    );
    const { mutate: mutateActionItemCreate } =
    ActionItemService.usePostCreateActionItem(persistedGroupBy.interactionId 
      ?? groupByValues?.groupByInteractionId ?? "", {
      onMutate: () => {
        setShowSpinner?.(true);
      },
      onSuccess: () => {
        // Correct invalidation based on type
          queryClient.invalidateQueries({ queryKey: ["get_my_action_items"] });
        showSnackbar("success", "Task created successfully");
        if(groupBy){
          queryClient.invalidateQueries({
            queryKey: ["get_group_by_action_items"],
          });
        }
        queryClient.invalidateQueries({
          queryKey: ["get_action_item_count"],
        });
  
        // Reset and close inline task row if open
        setInlineTaskData({
          title: "",
          assignee: persistedGroupBy.assignee 
            ?? groupByValues?.groupByAssigneeId 
            ?? loggedInProfileOrgId,
          dueDate: tomorrow,
          priority: "medium",
          status: persistedGroupBy.status ?? (loggedInUserDomain==="anarix.io" ? "open": "to_do"),
          tags: [],
          soloCoach: "",
          execSponsor: "",
          client: {
            orgId: persistedGroupBy.clientDomain?.orgId
              ?? null,
            name: persistedGroupBy.clientDomain?.clientName
              ?? null,
          },
        });
        setDueDateAnchorEl(null);
        setAddTaskAnchorEl(null);
      },
      onError: (error: string) => {
        showSnackbar("error", `Error while creating task: ${error}`);
      },
      onSettled: () => {
        setShowSpinner?.(false);
      },
    });

  useEffect(() => {
    const baseRows = generateRows(
      actionItems && actionItems.length > 0 ? actionItems : [],
      type,
      startEndMeetingTime,
      view,
      newUserTeamData,
      activeUsers,
      clientList,
      tagsList,
      spacesList,
      handleUnsubscribe,
    );

      if (showAddTask) {
        const addTaskRow = {
          id: "__add_task__",
          description: "+ Add Task",
          isAddRow: true,
          canEdit: false,
          isHidden: Boolean(addTaskAnchorEl),
        } as any;
    
        setRows([...baseRows, addTaskRow]);
      } else {
        setRows(baseRows);
      }
  }, [actionItems, tagsList, showAddTask, addTaskAnchorEl]);

  useEffect(() => {
    setShowSpinner?.(isLoading);
  }, [isLoading]);
   

  const onPaginationChange = (pageNumber: number) => {
    onPageChange(pageNumber + 1);
  };

  const onSortingChange = (sorting: any[]) => {
    if (sorting && sorting.length > 0) {
      sortorder && sortorder({
        sort_field: sorting[0].id,
        sort_order: sorting[0].desc === true ? "desc" : "asc",
      });
    }
  };
  

  const onGlobalFilterChange = (globalFilter: any) => {
    onSearch && onSearch(globalFilter);
  };

  const onFilterChange = (columnFilter: any[]) => {
    const filterSelections: {
      date: { start_time: string; end_time: string } | undefined;
      status: string | undefined;
    } = {
      date: undefined,
      status: undefined,
    };
    columnFilter.forEach((filter) => {
      if (filter.id === "revised_due_date") {
        const utcTimeOffset = getUTCTimeZoneOffset();
        const formattedStartDate = `${dayjs(filter.value).format(
          "YYYY-MM-DD"
        )}T00:00:00${utcTimeOffset}`;
        const formattedEndDate = `${dayjs(filter.value).format(
          "YYYY-MM-DD"
        )}T23:59:59${utcTimeOffset}`;
        filterSelections.date = {
          start_time: formattedStartDate,
          end_time: formattedEndDate,
        };
      }
      if (filter.id === "status") {
        const statusValues = filter.value.map((value: string) => {
          if (value === "Not Started") return "todo";
          else if (value === "In Progress") return "in_progress";
          else if (value === "Done") return "completed";
        });
        filterSelections.status = statusValues.join(",");
      }
    });
    onFilter(filterSelections);
  };

  const handleActionItemDelete = (actionItem: string) => {
    setActionItemToDelete(actionItem);
    setOpenDialog(true);
  };

  const handleConfirm = () => {
    mutateActionItemDelete({ actionItemId: actionItemToDelete.id });
  };

  const handleClose = () => {
    setOpenDialog(false);
    setActionItemToDelete(null);
  };
  const handlePageSizeChange = (newPageSize: number) => {
    onPageSizeChange && onPageSizeChange(newPageSize);
  };

  const handleInlineSave = () => {
  const currentSubscribers: string[] = [];
  // Add creator email if they are NOT the assignee
  if (
    typeof loggedInProfileOrgId === "string" &&
    loggedInProfileOrgId.trim() !== "" &&
    inlineTaskData.assignee !== loggedInProfileOrgId &&
    !currentSubscribers.includes(loggedInProfileOrgId)
  ) {
    currentSubscribers.push(loggedInProfileOrgId);
  }
  const actionItemCreatePayload = {
    ...(inlineTaskData.title && { title: inlineTaskData.title }),
    ...(inlineTaskData.assignee && { assigneePorId: inlineTaskData.assignee }),
    ...(inlineTaskData.dueDate && { dueDate: inlineTaskData.dueDate }),
    ...(inlineTaskData.priority && { priority: inlineTaskData.priority }),
    ...(inlineTaskData.status && { status: inlineTaskData.status }),

    ...(inlineTaskData.tags?.length > 0 && {
      tagIds: inlineTaskData.tags.map(tag => tag.tagId),
    }),

    ...(inlineTaskData.client?.orgId && {
      clientOrgId: inlineTaskData.client.orgId,
    }),

    ...(inlineTaskData.soloCoach && {
      coachPorId: inlineTaskData.soloCoach,
    }),

    ...(inlineTaskData.execSponsor && {
      sponsorPorId: inlineTaskData.execSponsor,
    }),

    ...(currentSubscribers.length > 0 && {
      actionSubscribersPorIds: currentSubscribers,
    }),
  };

    mutateActionItemCreate(actionItemCreatePayload);
    
  }
  const handleInlineCancel =()=>{
    setDueDateAnchorEl(null);
    setAddTaskAnchorEl(null)
             setInlineTaskData({
            title: "",
            assignee: persistedGroupBy.assignee 
              ?? groupByValues?.groupByAssigneeId 
              ?? loggedInProfileOrgId,
            dueDate: tomorrow,
            priority: "medium",
            status: persistedGroupBy.status ?? (loggedInUserDomain==="anarix.io" ? "open": "to_do"),
            tags: [] as Tag[],
            soloCoach: "",
            execSponsor: "",
            client: {
              orgId: persistedGroupBy.clientDomain?.orgId
                ?? null,
              name: persistedGroupBy.clientDomain?.clientName
                ?? null,
            },
          })
  }

  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);

  const normalizeUserValue = (
    val: string | string[] | null | undefined
  ): string => {
    if (Array.isArray(val)) return val[0] ?? "";
    return val ?? "";
  };

  const handleBulkSelectionChange = (ids: string[]) => {
    const nextIds = (ids || []).filter((id) => !isPlaceholderActionItemId(id));
    setSelectedActionItemIds?.(nextIds);
  };

  const hiddenSelectRowIds = useMemo(
    () =>
      rows
        .filter((row) => row?.isAddRow || isPlaceholderActionItemId(row?.id))
        .map((row) => row.id),
    [rows]
  );

  return (
    <ExclusiveColumnFilterContext.Provider value={exclusiveFilterValue}>
    <div id="action-items-table-container" style={{ position: "relative" }}>
      <Datatable
        columns={columns}
        rows={rows}
        rowCount={totalRecord}
        pageSize={pageSize}
        initialSort={initialSort}
        initialFilter={initialFilter}
        columnVisibility={combinedColumnVisibilty}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        onFilterChange={onFilterChange}
        onGlobalFilterChange={onGlobalFilterChange}
        timeRange={timeRange}
        calendarId={calendar?.calendar_id}
        actionItemType={actionItemType}
        onTypeChange={onTypeChange}
        onPageSizeChange={(newPageSize: number | null | undefined) => {
          if (newPageSize !== null && newPageSize !== undefined) {
            handlePageSizeChange(newPageSize);
          }
        }}
        selectedTab={selectedTab}
        rowHoverEnabled={true}
        minHeight={tableMinHeight}
        showSpinner ={showSpinner}
        pinnedColumns={pinnedColumns}
        tablePadding = {"0px 0px"}
        enableRowSelection = {enableRowSelection}
        onRowSelectionChange={handleBulkSelectionChange}
        selectedRowIds ={selectedActionItemIds}
        checkboxStyle={{ size: "small", color: "#6036D6" }}
        disabledRowIds={[
          ...(notAllowedActionItemids || []),
          "__add_task__",
          ...collectPlaceholderRowIds(rows),
        ]}
        hiddenSelectRowIds={hiddenSelectRowIds}
        inlineCreationRowId={addTaskAnchorEl ? "__add_task__" : undefined}
        maxVisibleRows={maxRowsConfig?.maxVisibleRows}
        rowHeight={maxRowsConfig?.rowHeight}
        pageSizeOptions={maxRowsConfig?.pageSizeOptions}
        maxHeight={tableMaxHeight}
        showRowCountLoading={isCountLoading}
        state={{ columnOrder }}
        onColumnOrderChange={onColumnOrderChange}
        enableColumnResizing={true}
        inlineCreationRow={
          addTaskAnchorEl ? (
            <InlineTaskCreation 
              onSave={handleInlineSave} 
              onClose={handleInlineCancel}
              taskData={inlineTaskData}
              setTaskData={setInlineTaskData}
              middleLeftComponents={[
                // {Assignee Selector}
                <div className="inline-chip" key="assignee">
                  <UserSelector
                    value={inlineTaskData.assignee}
                    onChange={(email) =>
                      setInlineTaskData((prev) => ({ ...prev, assignee: normalizeUserValue(email) }))
                    }
                    users={activeUsers || []}
                    variant="icon"
                    variantIcon={AssigneeIcon}
                    tooltipTitle="Solo Player"
                  />
                </div>,
            
              //  {DueDate Selector}
                <div className="inline-chip" key="due-date" >
                  <Tooltip title="Due Date" arrow placement="top">
                  <span
                    style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDueDateAnchorEl(e.currentTarget);
                    }}
                  >
                    <img src={DueDateIcon} className="inline-chip-icon" />

                    {inlineTaskData.dueDate && (
                      <span style={{ fontSize: "12px", marginLeft: "6px" }}>
                        {dayjs(inlineTaskData.dueDate).format("M/D/YYYY")}
                      </span>
                    )}
                  </span>
                  </Tooltip>
                  <CommonDatePicker 
                    value={inlineTaskData.dueDate}
                    anchorEl={dueDateAnchorEl}
                    onChange={(date) => {
                      const formattedDate = date
                      ? dayjs(date).local().format("YYYY-MM-DD")
                      : "";
                
                    setInlineTaskData((prev) => ({
                      ...prev,
                      dueDate: formattedDate,
                    }));
                      setDueDateAnchorEl(null);
                    }}
                    onClose={() => setDueDateAnchorEl(null)}  
                    />
                </div>,
                // {Priority Selector}
                <div className="inline-chip" key="priority">
                    <PrioritySelector
                      id="priority"
                      activeDropdownId={activeDropdownId}
                      setActiveDropdownId={setActiveDropdownId}
                      value={inlineTaskData.priority}
                      onChange={(priority) =>
                        setInlineTaskData((prev) => ({ ...prev, priority }))
                      }
                      options={PRIORITY_OPTIONS}
                      variant="icon"
                      tooltipTitle="Priority"
                    />
                </div>,
                // {Status Selector}
                <div className="inline-chip" key="status">
                  <ActionItemsStatusCell
                    value={inlineTaskData.status}
                    canEdit
                    onChange={(newStatus: string) => {
                      setInlineTaskData((prev) => ({
                        ...prev,
                        status: newStatus,
                      }));
                    }}
                    minWidth="auto"
                    onOpen={() => {}}
                    onClose={() => {}}
                    tooltipTitle="Status"
                  />
                </div>
              ]}
            
              middleRightComponents={[
                // {tags selector}
                <div className="inline-chip" key="tags">
                  <ActionItemsTagsCell
                    value={inlineTaskData.tags || []}
                    tagsList={tagsList || []}
                    onChange={(updatedTagIds: string[]) => {
                      const updatedTags = (tagsList || []).filter(tag =>
                        updatedTagIds.includes(tag.tagId)
                      );
                      setInlineTaskData(prev => ({
                        ...prev,
                        tags: updatedTags,
                      }));
                    }}
                    row={undefined}
                    canEdit = {true}
                    defaultIcon= {TagsListIcon}
                    inlineIconMode = {true}
                    noBorder= {true}
                    tooltipTitle="Tags"
                    />
                    </div>,
                // {Account Selector}
                <div className="inline-chip" key="account">
                  <AccountSelector
                    id="account"
                    activeDropdownId={activeDropdownId}
                    setActiveDropdownId={setActiveDropdownId}
                    value={inlineTaskData.client.name}
                    onChange={(orgId, clientName) =>
                      setInlineTaskData(prev => ({
                        ...prev,
                        client: {
                          orgId: orgId ?? null,
                          name: clientName ?? null,
                        },
                      }))
                    }
                    clientList={clientList || []}
                    icon={AccountIcon}
                    tooltipTitle="Account"
                  />
                </div>,
                // {Solo Coach}
                <div className="inline-chip" key="solo-coach">
                  <UserSelector
                    variant="icon"
                    variantIcon={SoloCoachIcon}
                    value={inlineTaskData.soloCoach}
                    onChange={(email) =>
                      setInlineTaskData((prev) => ({ ...prev, soloCoach: Array.isArray(email) ? email[0] ?? "" : email ?? ""}))
                    }
                    users={activeUsers || []}
                    tooltipTitle="Solo Coach"
                  />
                  </div>,
                // {Exec Sponsor}
                <div className="inline-chip" key="exec-sponser">
                    <UserSelector
                      value={inlineTaskData.execSponsor}
                      onChange={(email) =>
                        setInlineTaskData((prev) => ({ ...prev, execSponsor: Array.isArray(email) ? email[0] ?? "" : email ?? "" }))
                      }
                      users={activeUsers || []}
                      variant="icon"
                      variantIcon={ExecSponseIcon}
                      tooltipTitle="Exec-Sponsor"
                    />
                </div>,
              ]}
            />
          ) : undefined
        }
      />

      <CustomConfirm
        title="Confirmation"
        message={"Are you sure you want to delete this task?"}
        isOpen={openDialog}
        handleSuccess={handleConfirm}
        handleClose={handleClose}
        actionBtnLabel={"Confirm"}
      />
      <FeedbackPopup
        open={discardPopupOpen}
        interactionId={
          discardRow?.original?.meeting_id}
        feedbackKey="client_action_item"
        feedbackReference={feedbackReference}
        onClose={() => {
          setDiscardPopupOpen(false) 
          setFeedbackReference("")
        }}
        onSubmit={handleDiscardSubmit}
      />
      </div>
    </ExclusiveColumnFilterContext.Provider>
  );
};

export default ActionItems;
